// =====================
// Contract Approval Requests
// =====================
//
// Internal, staff-side approval that the contract.settings.requireApproval
// toggle promises: while the setting is on, a contract cannot transition into
// SIGNED/ACTIVE without a current APPROVED request. Approvals are event rows
// and request status is DERIVED from them under the launch policy "one
// APPROVED completes; one REJECTED rejects"; the derivation lives in one API
// service function so a future multi-approver policy never touches these
// shapes. Same lineage as budget-change.ts and the portal invoice-approval
// pass — one approval idiom across the codebase, separate tables per object.

import { z } from 'zod'

export type ContractApprovalRequestStatus =
  | 'PENDING'
  | 'APPROVED'
  | 'REJECTED'
  | 'CANCELLED'

export type ContractApprovalDecision = 'APPROVED' | 'REJECTED'

// ---- Request bodies ----

export const CreateContractApprovalRequestSchema = z.object({
  contractId: z.string().uuid(),
  note: z.string().max(2000).optional(),
})

export type CreateContractApprovalRequestInput = z.infer<
  typeof CreateContractApprovalRequestSchema
>

export const DecideContractApprovalSchema = z.object({
  comment: z.string().max(2000).optional(),
})

export type DecideContractApprovalInput = z.infer<
  typeof DecideContractApprovalSchema
>

export const ListContractApprovalRequestsSchema = z.object({
  contractId: z.string().uuid().optional(),
  status: z.enum(['PENDING', 'APPROVED', 'REJECTED', 'CANCELLED']).optional(),
})

export type ListContractApprovalRequestsInput = z.infer<
  typeof ListContractApprovalRequestsSchema
>

// ---- Responses ----

export interface ContractApprovalEvent {
  id: string
  decidedBy: string
  decidedByName: string | null
  decision: ContractApprovalDecision
  comment: string | null
  createdAt: string
}

export interface ContractApprovalRequestWithTrail {
  id: string
  organizationId: string
  contractId: string
  contractTitle: string | null
  note: string | null
  status: ContractApprovalRequestStatus
  createdBy: string
  createdByName: string | null
  createdAt: string
  updatedAt: string
  approvals: ContractApprovalEvent[]
}
